<script setup>
import { DateTime } from 'luxon';
import { Mask } from 'maska';
import { storeToRefs } from 'pinia';
import useEventDataStore from '../stores/eventDataStore';
import useFeatureFlagStore from '../stores/featureFlagStore';
import EventDateTimeLocation from '../dashboards/src/components/EventDateTimeLocation.vue';
import BaseUrl from '~/api/BaseUrl.js';
import ENDPOINTS from '~/api/endpoints/index.js';
import Headers from '~/api/Headers.js';
import isEqual from 'lodash/isEqual.js';
import { computed } from 'vue';
import RegistrationUnavailableAlert from '~/components/attendance/RegistrationUnavailableAlert.vue';

const mask = new Mask({ mask: '(###) ###-####' });

const router = useRouter();
const route = useRoute();
const { eventData, error: errorLoadingData } = storeToRefs(useEventDataStore());
const eventDataStore = useEventDataStore();

const eventDataUrl = `${BaseUrl.get(ENDPOINTS.EVENTS.GET_EVENT_PAGE_CONFIG)}${
  ENDPOINTS.EVENTS.GET_EVENT_PAGE_CONFIG
}?event_id=${route.params.id || route.params.eventId}`;
// NOTE: key/immediate must live in the options object — a third argument is
// ignored by useFetch, which made the request fire twice (once on setup,
// once via execute) with no cache key (FEP-7383)
const {
  data,
  error: apiError,
  execute,
} = await useFetch(eventDataUrl, {
  headers: Headers,
  key: `event-config-${route.params.id || route.params.eventId}`,
  immediate: false,
});

await execute();

if ((!eventData.value && data) || !isEqual(data.value, eventData.value)) {
  nextTick(() => {
    eventDataStore?.setEventData(data?.value ?? {}, apiError?.value ?? {});
    setTimeZone(data);
  });
}
const eventTimeZone = ref(null);

const setTimeZone = (eventData) => {
  if (eventData?.value?.event_timezone) {
    eventTimeZone.value = DateTime.fromISO(eventData.value?.event_start_date, {
      zone: eventData.value?.event_timezone,
    }).toFormat('ZZZZ');
  }
};

watch(
  eventData,
  () => {
    setTimeZone(eventData);
  },
  { deep: true },
);

function stripHtml(html) {
  if (!html) return '';
  try {
    if (typeof document !== 'undefined') {
      const div = document.createElement('div');
      div.innerHTML = html;
      return (div.textContent || div.innerText || '').trim();
    }
    // Fallback for non-DOM environments
    return String(html)
      .replace(/<[^>]+>/g, '')
      .trim();
  } catch (e) {
    return String(html)
      .replace(/<[^>]+>/g, '')
      .trim();
  }
}
const requestUrl = useRequestURL();
useHead({
  title: `${data?.value?.event_title || 'FundEasy'}`,
  meta: [
    {
      property: 'og:title',
      content: data?.value?.event_title,
    },
    { property: 'og:image', content: data?.value?.facebook_banner_image },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: requestUrl.href },
    { property: 'og:site_name', content: 'FundEasy' },
    { property: 'og:description', content: stripHtml(data?.value?.content_dynamic_body) },
    { property: 'twitter:title', content: data?.value?.event_title },
    { property: 'twitter:description', content: stripHtml(data?.value?.content_dynamic_body) },
    { property: 'twitter:image', content: data?.value?.banner_image },
    { property: 'fb:app_id', content: 190161947775428 },
  ],
});
const flagDataStore = useFeatureFlagStore();
const { flagData, error: errorLoadingFlagData } = storeToRefs(flagDataStore);

let apiEnvironment = '';

switch (true) {
  case window?.location.hostname.includes('v4app'):
    apiEnvironment = 'production';
    break;
  case window?.location.hostname.includes('beta'):
    apiEnvironment = 'beta';
    break;
  case window?.location.hostname.includes('qa'):
    apiEnvironment = 'qa';
    break;
  case window?.location.hostname.includes('staging'):
    apiEnvironment = 'staging';
    break;
  default:
    apiEnvironment = 'localhost';
}

const featureFlagsUrl = computed(
  () =>
    `${BaseUrl.get(ENDPOINTS.META.SETTINGS)}${ENDPOINTS.META.SETTINGS}?id=${apiEnvironment}_frontend&event_id=${
      route.params.id
    }`,
);

const {
  data: featureFlags,
  error: errorGettingFeatureFlags,
  execute: executeFlags,
} = await useFetch(
  featureFlagsUrl,
  {
    headers: Headers,
  },
  {
    key: Date.now(),
  },
);
await executeFlags();

if ((!flagData?.value && featureFlags) || !isEqual(featureFlags?.value, flagData?.value)) {
  nextTick(() =>
    flagDataStore?.setFlagsData(featureFlags?.value ?? {}, errorGettingFeatureFlags?.value ?? {}),
  );
}

onMounted(() => {
  document.addEventListener('wheel', function (event) {
    if (document.activeElement.type === 'number' && !window.disabledNumberScroll) {
      document.activeElement.blur();
      window.disabledNumberScroll = true;
    }
  });
});

const { data: ticketData } = await useFetch(
  `${BaseUrl.get(ENDPOINTS.EVENTS.GET_ALL_SELECTIONS)}${ENDPOINTS.EVENTS.GET_ALL_SELECTIONS}?event_id=${
    route.params.id
  }`,
  { headers: Headers },
  { key: Date.now() },
);

const isRecruiterMode = computed(() => !!data?.value?.recruiter_mode);
const isRegistrationOpen = computed(
  () => data?.value?.registration_open ?? data?.registration_open,
);
const shouldShowGuestOption = computed(
  () =>
    Array.isArray(ticketData.value) && ticketData.value.some((el) => el.ticket_id && !el.table_id),
);
const shouldShowTableOption = computed(
  () => Array.isArray(ticketData.value) && ticketData.value.some((el) => el.table_id),
);
const shouldShowSponsorOption = computed(() => !eventData.value?.disableSponsorReg);

const handleSelectionClick = (filter) => {
  // NOTE: sponsors deliberately stay behind this redirect. `registration_open` is exactly
  // `event.status === 'Open'` (events.mapper.js), which is the same condition
  // POST /events/public-page/sponsor-order enforces before it will accept an order. Letting a
  // sponsor through to the form on a closed event only gets them to a submit that throws
  // 'Event is not open for registration'. Opening sponsorship on a closed event is an API
  // change first, not a routing one.
  if (!isRegistrationOpen.value) {
    router.push({
      name: 'attendance-id-registration-closed',
      params: { id: route.params.id },
    });
    return;
  }

  if (filter === 'sponsors' && shouldShowSponsorOption.value) {
    return router.push({
      name: 'attendance-id-sponsor',
      params: { id: route.params.id },
    });
  }

  return router.push({
    name: 'attendance-id-registration',
    params: { id: route.params.id },
    query: { selectedFilter: filter, invite_code: route.query?.invite_code },
  });
};

const isSameDate = (date1, date2) => {
  const timezone = data.value?.event_timezone || 'UTC';
  const d1 = DateTime.fromISO(date1).setZone(timezone);
  const d2 = DateTime.fromISO(date2).setZone(timezone);
  return d1.hasSame(d2, 'day');
};
</script>

<template>
  <v-container
    class="pa-0"
    :style="{
      backgroundColor: data?.event_color || 'none',
      position: 'relative',
      margin: 0,
      maxWidth: '100vw',
      minHeight: '100%',
      overflow: 'auto',
    }"
    v-if="data !== undefined && data !== null && Object.keys(data)?.length > 0"
  >
    <v-row no-gutters style="height: 100%; width: 100%" justify="center">
      <v-col sm="12" :style="`background-color:#E5E7EB; position:relative`">
        <div
          v-if="data?.banner_image"
          class="backdrop"
          :style="{
            backgroundImage: `linear-gradient(rgba(211, 211, 211, 0.45), rgba(211, 211, 211, 0.45)),url(${data?.banner_image})`,
          }"
        />
        <main
          class="w-100 d-flex flex-column pa-0 pa-sm-4 pa-lg-16"
          style="position: relative; top: 0; overflow: auto"
        >
          <div class="w-100">
            <img :src="data?.banner_image" style="height: 100%; width: 100%" />
          </div>

          <v-row
            class="mb-6"
            no-gutters
            style="background-color: var(--fundeasy-colors-generic-00)"
          >
            <v-col class="pa-4 pa-lg-12">
              <v-row
                class="mb-4 mb-lg-12"
                no-gutters
                v-if="
                  route.name !== 'attendance-id-decline-invitation-confirmation' &&
                  route.name !== 'attendance-id-confirmation-cartId'
                "
              >
                <v-col>
                  <v-row
                    v-if="route.name !== 'attendance-id-decline-invitation-invitationId'"
                    no-gutters
                    class="text-center text-md-start align-center"
                    :style="{
                      'justify-content':
                        route.name === 'attendance-id-registration-closed' ||
                        route.name === 'attendance-id-confirmation-cartId' ||
                        route.name === 'attendance-id-express-checkin' ||
                        route.name === 'attendance-id-decline-invitation-invitationId'
                          ? 'center'
                          : '',
                    }"
                    :class="{
                      'fs-14': $vuetify.display.xs,
                      'justify-center':
                        route.name === 'attendance-id-registration-closed' ||
                        route.name === 'attendance-id-confirmation-cartId' ||
                        route.name === 'attendance-id-express-checkin' ||
                        route.name === 'attendance-id-decline-invitation-invitationId',
                      'justify-lg-start':
                        route.name !== 'attendance-id-registration-closed' &&
                        route.name !== 'attendance-id-confirmation-cartId' &&
                        route.name !== 'attendance-id-express-checkin' &&
                        route.name !== 'attendance-id-decline-invitation-invitationId',
                    }"
                  >
                    <img
                      class="mr-3"
                      style="width: 20px; height: 20px"
                      :src="data?.account_logo ? data?.account_logo : '/assets/FE_icon.svg'"
                      alt="Account Logo"
                    />
                    <p style="color: var(--fundeasy-colors-neutral-500)">
                      {{ data?.account_name }}
                    </p>
                  </v-row>
                  <v-row
                    no-gutters
                    :class="{
                      'fs-14': $vuetify.display.xs,
                      'fs-36': $vuetify.display.mdAndUp,
                      'fs-28': $vuetify.display.sm,
                      'fs-24': $vuetify.display.xs,
                      'justify-center':
                        route.name === 'attendance-id-registration-closed' ||
                        route.name === 'attendance-id-confirmation-cartId' ||
                        route.name === 'attendance-id-express-checkin' ||
                        route.name === 'attendance-id-decline-invitation-invitationId',
                      'justify-lg-start':
                        route.name !== 'attendance-id-registration-closed' &&
                        route.name !== 'attendance-id-confirmation-cartId' &&
                        route.name !== 'attendance-id-express-checkin' &&
                        route.name !== 'attendance-id-decline-invitation-invitationId',
                    }"
                  >
                    <h1
                      class="my-2"
                      :class="{
                        'fs-36': $vuetify.display.mdAndUp,
                        'fs-28': $vuetify.display.sm,
                        'fs-24': $vuetify.display.xs,
                      }"
                    >
                      {{ data?.event_title }}
                    </h1>
                  </v-row>
                  <v-row
                    no-gutters
                    :class="{
                      'fs-14': $vuetify.display.xs,
                      'justify-center':
                        route.name === 'attendance-id-registration-closed' ||
                        route.name === 'attendance-id-confirmation-cartId' ||
                        route.name === 'attendance-id-express-checkin' ||
                        route.name === 'attendance-id-decline-invitation-invitationId',
                      'justify-lg-start':
                        route.name !== 'attendance-id-registration-closed' &&
                        route.name !== 'attendance-id-confirmation-cartId' &&
                        route.name !== 'attendance-id-express-checkin' &&
                        route.name !== 'attendance-id-decline-invitation-invitationId',
                    }"
                    class="fs-14 mb-4 mb-lg-0 text-center align-center"
                  >
                    <EventDateTimeLocation
                      :startDate="data?.event_start_date"
                      :endDate="data?.event_end_date"
                      :timezone="data?.event_timezone"
                      :isVirtual="data?.location_details?.isVirtual"
                      :city="data?.location_details?.city"
                      :state="data?.location_details?.state"
                    />
                  </v-row>

                  <div
                    v-if="$route.name === 'attendance-id-event'"
                    class="d-flex d-lg-none justify-center"
                  >
                    <v-col cols="12">
                      <div v-if="isRecruiterMode">
                        <RegistrationUnavailableAlert
                          v-if="eventData?.payment_processing_available === false"
                          class="mb-3"
                        />
                        <v-tooltip
                          :disabled="isRegistrationOpen && shouldShowGuestOption"
                          location="top"
                          content-class="white-tooltip"
                        >
                          <template #activator="{ props }">
                            <span v-bind="props" class="d-inline-block w-100">
                              <v-btn
                                variant="flat"
                                :color="data?.event_color || '#0E6CB3'"
                                block
                                class="text-transform-none"
                                :disabled="!(isRegistrationOpen && shouldShowGuestOption)"
                                :style="{
                                  pointerEvents:
                                    isRegistrationOpen && shouldShowGuestOption ? 'auto' : 'none',
                                }"
                                @click="handleSelectionClick('tickets')"
                              >
                                Register
                              </v-btn>
                            </span>
                          </template>
                          {{
                            data?.registration_page_content?.closed_registration_tooltip ||
                            'Registration is closed.'
                          }}
                        </v-tooltip>
                        <!-- FUN-113: recruiter mode renders one Register CTA instead of the card
                             list, so the sponsor option had nowhere to surface -- and Vision
                             Dinner, the template this ticket exists for, is the only
                             recruiter-mode template. Gated the same way the card list gates its
                             sponsor button -- see the note in handleSelectionClick for why
                             `isRegistrationOpen` belongs here too. -->
                        <v-btn
                          v-if="shouldShowSponsorOption"
                          variant="outlined"
                          :color="data?.event_color || '#0E6CB3'"
                          block
                          class="text-transform-none mt-3"
                          append-icon="mdi-arrow-right"
                          :disabled="!isRegistrationOpen"
                          :style="{ pointerEvents: isRegistrationOpen ? 'auto' : 'none' }"
                          @click="handleSelectionClick('sponsors')"
                        >
                          {{
                            data?.registration_page_content?.sponsor_button_prefix ||
                            'Register as Sponsor'
                          }}
                        </v-btn>
                      </div>
                      <AttendanceRegisterButtonActions
                        v-else
                        :eventData="eventData"
                        :shouldShowGuestOption="shouldShowGuestOption"
                        :shouldShowTableOption="shouldShowTableOption"
                        :shouldShowSponsorOption="shouldShowSponsorOption"
                        :handleSelectionClick="handleSelectionClick"
                      />
                    </v-col>
                  </div>
                </v-col>
              </v-row>
              <div>
                <slot v-if="!errorLoadingData" />
              </div>
            </v-col>

            <v-col
              cols="12"
              lg="4"
              v-if="
                $route.name === 'attendance-id-event' &&
                !data.event_preferences?.disableParticipantsRegs
              "
            >
              <aside class="pa-lg-12 pa-4">
                <template v-if="isRecruiterMode">
                  <RegistrationUnavailableAlert
                    v-if="eventData?.payment_processing_available === false"
                    class="mb-3"
                  />
                  <v-tooltip
                    :disabled="isRegistrationOpen && shouldShowGuestOption"
                    location="top"
                    content-class="white-tooltip"
                  >
                    <template #activator="{ props }">
                      <span v-bind="props" class="d-none d-lg-block w-100">
                        <v-btn
                          class="text-transform-none"
                          :color="data?.event_color || '#0E6CB3'"
                          block
                          :disabled="!(isRegistrationOpen && shouldShowGuestOption)"
                          :style="{
                            pointerEvents:
                              isRegistrationOpen && shouldShowGuestOption ? 'auto' : 'none',
                          }"
                          @click="handleSelectionClick('tickets')"
                        >
                          Register
                        </v-btn>
                      </span>
                    </template>
                    {{
                      data?.registration_page_content?.closed_registration_tooltip ||
                      'Registration is closed.'
                    }}
                  </v-tooltip>
                  <!-- FUN-113: see the matching block in the mobile column above. The breakpoint
                       classes sit on a wrapping span, as they do on the Register button: v-btn is
                       display:inline-grid and `d-lg-block` would override that, breaking how it
                       centres its own content. -->
                  <span v-if="shouldShowSponsorOption" class="d-none d-lg-block w-100">
                    <v-btn
                      variant="outlined"
                      :color="data?.event_color || '#0E6CB3'"
                      block
                      class="text-transform-none mt-3"
                      append-icon="mdi-arrow-right"
                      :disabled="!isRegistrationOpen"
                      :style="{ pointerEvents: isRegistrationOpen ? 'auto' : 'none' }"
                      @click="handleSelectionClick('sponsors')"
                    >
                      {{
                        data?.registration_page_content?.sponsor_button_prefix ||
                        'Register as Sponsor'
                      }}
                    </v-btn>
                  </span>
                </template>
                <AttendanceRegisterButtonActions
                  v-else
                  class="d-none d-lg-block"
                  :eventData="eventData"
                  :shouldShowGuestOption="shouldShowGuestOption"
                  :shouldShowTableOption="shouldShowTableOption"
                  :shouldShowSponsorOption="shouldShowSponsorOption"
                  :handleSelectionClick="handleSelectionClick"
                />
              </aside>
            </v-col>

            <v-col style="width: fit-content; max-width: fit-content" v-if="$slots['main-aside']">
              <slot name="main-aside"></slot>
            </v-col>
          </v-row>

          <!-- footer  -->
          <v-row
            no-gutters
            style="background-color: var(--fundeasy-colors-generic-00)"
            class="fs-14 pl-12 pr-12 pt-4 pb-4"
          >
            <v-col align-self="center">
              <img style="width: 160px" src="/fundeasy_logo.png" alt="FundEasy Logo" />
              <p class="pl-2" style="color: #818181">
                Powered by <a href="https://fundeasy.com" target="_blank">FundEasy</a> ©
                {{ new Date().getFullYear() }}. All rights reserved.
              </p>
            </v-col>
            <v-col align-self="center" class="pl-2" sm="12" lg="2">
              <v-row no-gutters>
                <v-col cols="12" v-if="data?.promotional_information?.org_website">
                  <a :href="data?.promotional_information?.org_website" target="_blank">
                    {{ data?.promotional_information?.org_website }}
                  </a>
                </v-col>
                <v-col cols="12" v-if="data?.point_of_contact?.primary_event_contact_first_name">
                  <p>
                    {{ data?.point_of_contact?.primary_event_contact_first_name }}
                    {{ data?.point_of_contact?.primary_event_contact_last_name }}
                  </p>
                </v-col>
                <v-col cols="12" v-if="data?.point_of_contact?.primary_event_contact_phone_number">
                  <p>
                    {{
                      mask.masked(
                        String(data?.point_of_contact?.primary_event_contact_phone_number),
                      )
                    }}
                    <span v-if="data?.point_of_contact?.primary_event_contact_phone_ext">
                      x{{ data?.point_of_contact?.primary_event_contact_phone_ext }}
                    </span>
                  </p>
                </v-col>
                <v-col cols="12">
                  <a :href="`mailto:${data?.point_of_contact?.primary_event_contact_email}`">
                    {{ data?.point_of_contact?.primary_event_contact_email }}
                  </a>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </main>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped>
.backdrop {
  min-height: 400px;
  height: 400px;
  min-width: 100%;
  position: absolute;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  filter: blur(8px);
  -webkit-filter: blur(8px);
  z-index: 0;
  top: 0;
  left: 0;
}

.mb-4 img {
  width: 100%;
}

a {
  color: var(--fundeasy-colors-blue-900);
}
</style>
<style>
@import '@/AppVariables.css';
</style>
